import { useEffect, useRef } from 'react';
import { useLocation } from '@/lib/router-compat';

/**
 * Smooth fade+slide page transition between routes.
 * Skips first mount to avoid double-animating the initial paint.
 *
 * GSAP is dynamically imported inside the effect so it is NOT bundled into
 * the main chunk (kept off the critical path for LCP/INP on mobile).
 */
export function useGsapPageTransition() {
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Skip first paint — let SSR/critical content stay static for LCP.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const { gsap } = await import('@/lib/gsap');
      if (cancelled || !el) return;
      const ctx = gsap.context(() => {
        gsap.fromTo(
          el,
          { opacity: 0, y: 12 },
          { opacity: 1, y: 0, duration: 0.45, ease: 'power2.out', clearProps: 'transform' }
        );
      }, el);
      cleanup = () => ctx.revert();
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [location.pathname]);

  return ref;
}
